import { useMemo, useState } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import useDocumentViewViewModel, { VIEW_MODES } from './useDocumentViewViewModel'
import { useLabelStudio } from '@hook/useLabelStudio'
import { downloadDocumentById } from '@utils'
import { GCS_BUCKET_DOCUMENTS, GCS_BUCKET_INDEX_DOCUMENTS, RECORD_TYPE_DOCUMENT, DOCUMENT_TYPES } from '@constants'
import cn from 'clsx'

import Title from '@components/shared/Title'
import CircleSpinner from '@components/shared/CircleSpinner'
import AlertModal from '@components/shared/AlertModal'
import DocumentPagesViewer from '@components/shared/DocumentPagesViewer'
import DocumentViewBreadcrumbs from '@components/collection/DocumentView/views/DocumentViewBreadcrumbs'
import DocumentViewHeaderToolbar from '@components/collection/DocumentView/views/DocumentViewHeaderToolbar'
import LabelStudioEditor from '@components/shared/LabelStudioEditor'
import SingleComment from '@/components/shared/CommentsDrawer/SingleComment'
import { MdHelpOutline } from 'react-icons/md'
import { TAB_BROWSE_KEY, TAB_SEARCH_INDEX_KEY } from '../Collection/useCollectionViewModel'
import Checkbox from '$components/Forms/Checkbox'
import LegalDescriptionModal from '@/components/legaldescription/LegalDescriptionModal'
import { Selectbox } from '@/components/shared/Selectbox'
import './DocumentView.css'
import VerticalPagination from '@/components/shared/VerticalPagination'
import ProcessDocumentsModal from './ProcessDocumentsModal'


// @todo @lucas is this needed anymore? the button was commented
const showEditDisabledHelpButton = false

/**
 * Note: this is the legacy, full-page document view
 * We're mostly migrating to `DocumentViewerV2` and it's popup wrapper component
 * 
 * @returns {JSX.Element}
 * @constructor
 */
const DocumentView = () => {
    const [showModal, showHideModal] = useState(true)
    const [showLegalsModal, setShowLegalsModal] = useState(false)
    const vm = useDocumentViewViewModel()
    const pdfLoading = useWireValue(store.pdfRendererLoading)
    
    const labelStudioEnabled = useMemo(() => (
        vm.isEditMode && vm.imageUrl?.length > 0
    ), [vm.isEditMode, vm.imageUrl])
    
    const labelStudioReady = useMemo(() => (
        labelStudioEnabled &&
        vm.record?.id?.length > 0 &&
        Boolean(vm.editorNode)
    ), [
        labelStudioEnabled,
        vm.record,
        vm.editorNode,
    ])
    
    const labelStudioConfig = useMemo(() => !labelStudioReady ? {
        enabled: false,
        recordType: RECORD_TYPE_DOCUMENT,
    } : {
        enabled: labelStudioEnabled,
        url: vm.imageUrl,
        gcsBucket: vm.record?.collection?.isParse ? GCS_BUCKET_INDEX_DOCUMENTS : GCS_BUCKET_DOCUMENTS,
        editorNode: vm.editorNode,
        record: vm.record,
        setRecord: vm.setRecord,
        recordType: RECORD_TYPE_DOCUMENT,
        pageIndex: vm.pageIndex,
        showAllLabels: vm.showAllLabelsChecked,
        showPrivateLabels: vm.showPrivateLabelsChecked,
        useOrganizationLabels: false,
        useCollectionLabels: true,
    }, [
        labelStudioReady,
        vm.record,
        vm.setRecord,
        vm.pageIndex,
        vm.editorNode,
        vm.showAllLabelsChecked,
        vm.showPrivateLabelsChecked,
    ])
    
    const {
        labelStudioError,
        labelStudioSaving,
        hasAssignedLabels,
        annotationExists,
        verifyUnsavedChanges,
    } = useLabelStudio(labelStudioConfig)
    
    /**
     * Actual page count to use
     * Since the `pageCount` from the ViewModel is only updated if the PDF renderer
     * hook runs, links that go directly to edit mode (LabelStudio) don't have the page count set
     * 
     * @type {Number}
     */
    const pageCount = useMemo(() => {
        
        // This shouldn't happen, but if it does, it might mean our
        // data is incorrect, so at least warn, so we can investigate
        if (vm.pageCount > 0 && vm.record?.pages?.length > 0 && vm.pageCount !== vm.record?.pages?.length)
            console.warn('Possible mismatch between PDF reported & database record page counts')
        
        return vm.pageCount || vm.record?.pages?.length || 0
        
    }, [vm.pageCount, vm.record])
    
    const collectionLink = useMemo(() => vm.record?.collection && (
        vm.record?.collection?.isParse
            ? `/collection/${vm.record.collection.id}/${TAB_SEARCH_INDEX_KEY}`
            : `/collection/${vm.record.collection.id}/${TAB_BROWSE_KEY}`
    ), [vm.record])
    
    if (labelStudioError) return (
        <div className="DocumentView">
            <p className="absolute-centered">
                There was a problem loading the annotations editor.
            </p>
        </div>
    )
    
    if (!vm.record) return (
        <div className="DocumentView">
            <div className="absolute-centered flex flex-col justify-center items-center">
                <CircleSpinner />
            </div>
        </div>
    )
    
    if (vm.error) return (
        <div className="DocumentView">
            <div className="text-center absolute-centered">
                {vm.error}
            </div>
        </div>
    )
    
    if (vm.loadingCapture || labelStudioSaving) {
        // eslint-disable-next-line no-restricted-globals
        document.body.classList.add('overflow-hidden')
    } else {
        // eslint-disable-next-line no-restricted-globals
        document.body.classList.remove('overflow-hidden')
    }
    
    return (
        
        <div ref={vm.refContainer} className={cn('DocumentView', {
            'edit-mode': vm.isEditMode,
        })}>
            
            <Title>{vm.record?.name ?? 'Document'}</Title>
            
            <header className="flex items-center content-center justify-between pb-3 px-3 sticky top-0 bg-base-100">
                
                <div className="flex justify-center items-center gap-3 absolute h-12 top-[52px] right-[420px] px-3">
                    <div className="flex justify-center items-center text-nowrap gap-3">
                        <span>Document Type</span>
                        <Selectbox
                            className="w-[200px]"
                            name="documentType"
                            options={DOCUMENT_TYPES}
                            option={vm.documentType}
                            handleChange={async (n, v) => {
                                vm.setDocumentType(v)
                                
                                const documentId = vm.record?.id
                                
                                await actions.saveDocumentType(documentId, v.id)
                            }} />
                    </div>
                    
                    <div className="flex gap-[1px]">
                        {!!vm.record?.legalDescriptions?.length &&
                                <button
                                    className="btn btn-neutral cursor-pointer rounded-e-none"
                                    onClick={() => setShowLegalsModal(true)}>
                                    View/Edit Legal Descriptions
                                </button>
                        }
                        
                        {vm.isEditMode && <SingleComment
                            noRadius={!!vm.record?.legalDescriptions?.length}
                            documentId={vm.record?.id} />}
                    </div>
                
                </div>
                
                <DocumentViewBreadcrumbs
                    recordName={vm.record?.name ?? ''}
                    collectionName={vm.record?.collection?.name ?? ''}
                    collectionLink={collectionLink} />
                
                
                <div className="flex items-center justify-end gap-2">
                    
                    {vm.isEditMode && (
                        <DocumentViewHeaderToolbar
                            isParseAdmin={vm.isParseAdmin}
                            isFullScreen={vm.isFullScreen}
                            toggleFullScreen={vm.toggleFullScreen}
                            hasAssignedLabels={hasAssignedLabels}
                            showAllLabelsChecked={vm.showAllLabelsChecked}
                            setShowAllLabelsChecked={vm.setShowAllLabelsChecked}
                            showPrivateLabelsChecked={vm.showPrivateLabelsChecked}
                            setShowPrivateLabelsChecked={vm.setShowPrivateLabelsChecked}
                            showExpandedAnnotationView={vm.showExpandedAnnotationView}
                            setShowExpandedAnnotationView={vm.setShowExpandedAnnotationView} />
                    )}
                    
                    {vm.isEditMode && vm.record?.collection &&
                        <div className="border rounded-md border-secondary px-3">
                            <Checkbox
                                checked={vm.record.annotationCompleted}
                                label="Mark as Done"
                                labelRtl
                                labelProps={{ className: 'h-[36.55px] Checkbox-label cursor-pointer' }}
                                onChange={() => vm.toggleAnnotationCompleted(vm.record.id)} />
                        </div>
                    }
                    
                    {vm.record?.collection && (
                        <button
                            className="btn btn-secondary"
                            onClick={() => downloadDocumentById(vm.record?.id)}
                            disabled={!vm.pdfUrl}>
                            Download
                        </button>
                    )}
                    <button className="btn btn-secondary"
                        data-tooltip-id="default-tip"
                        data-tooltip-content="Captures the text for the drawn boxes in the page."
                        data-tooltip-place="bottom"
                        disabled={vm.loadingCapture}
                        onClick={() => vm.captureBboxText()}>
                            Capture
                    </button>
                    
                    
                    
                    {/*
                    this might not be necessary as we're not toggling between view/edit anymore
                    <Link
                        className="btn btn-secondary"
                        to={collectionLink}
                        disabled={!vm.record?.pages?.length}
                        onClick={vm.toggleEditMode}>
                        {vm.isEditMode ? 'Close Editor' : 'Edit Document'}
                    </Link> */}
                    
                    {showEditDisabledHelpButton && !vm.isEditMode && !vm.canEditDocument && (
                        <button
                            className="btn btn-ghost"
                            onClick={() => vm.setEditDisabledHelpDialogOpen(true)}>
                            <MdHelpOutline className="text-muted" style={{ fontSize: '1.7rem' }} />
                        </button>
                    )}
                
                </div>
            
            </header>
            
            <div className="w-10 bg-base-200 flex flex-col items-center 
                rounded-md gap-3 fixed top-[calc(70%+5px)] right-[435px] verticalPagination">
                <VerticalPagination
                    pageIndex={vm.pageIndex}
                    setPageIndex={vm.setPageIndex}
                    pageCount={pageCount}
                    conditionFnToChangePage={verifyUnsavedChanges} />
            </div>
            
            {(vm.loadingCapture || labelStudioSaving) &&
            <div className={cn('backdrop-blur-sm fixed left-0 w-screen loading-overlay', {
                'h-full top-0': labelStudioSaving,
                'h-[calc(100vh-124px)]': vm.loadingCapture,
            })}>
                <div className="absolute-centered flex flex-col items-center gap-3 p-4 rounded bg-base-300">
                    <CircleSpinner />
                    <div className="text-2xl">{vm.loadingCapture ? 'Capturing annotations' : 'Updating'}</div>
                </div>
            </div>}
            
            {vm.isEditMode && (
                <LabelStudioEditor
                    ref={vm.refEditor}
                    error={labelStudioError}
                    isSaving={labelStudioSaving} />
            )}
            
            <div className={cn('canvas-wrap relative', {
                hidden: vm.isEditMode || vm.viewMode !== VIEW_MODES.PDF,
            })}>
                <canvas ref={vm.refCanvas} />
                {pdfLoading && (
                    <CircleSpinner className="absolute-centered" />
                )}
            </div>
            
            {!vm.isEditMode && vm.viewMode === VIEW_MODES.PNG && (
                <DocumentPagesViewer
                    pageUrls={vm.documentPageUrls}
                    pageIndex={vm.pageIndex}
                    setPageIndex={vm.setPageIndex} />
            )}
            
            {!vm.pdfUrl &&
            <div className="flex items-center justify-center h-screen">
                <p>Image Not Available</p>
            </div>}
            
            <AlertModal
                showModal={vm.editDisabledHelpDialogOpen}
                showHideModal={vm.setEditDisabledHelpDialogOpen}
                title="Document Processing"
                message={!vm.record?.pages?.length ? 
                    'This document is still being processed. Please come back in a few minutes.' :
                    'This document is still being processed. You can edit it now, or wait until it\'s finished.'
                } />
            
            <LegalDescriptionModal open={showLegalsModal} setOpen={setShowLegalsModal} document={vm.record} />
            
            {(!annotationExists && !actions.hasPromptedToProcessDocumentId(vm.record.id)) && (
                <ProcessDocumentsModal
                    showState={[showModal, showHideModal]}
                    vm={vm}/>
            )}
        
        </div>
        
    )
    
}

export default DocumentView
